import React from 'react';
import Alert from '@mui/joy/Alert';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import FormControl from '@mui/joy/FormControl';
import FormHelperText from '@mui/joy/FormHelperText';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Option from '@mui/joy/Option';
import Radio from '@mui/joy/Radio';
import RadioGroup from '@mui/joy/RadioGroup';
import Select from '@mui/joy/Select';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import {NotificationService} from '../../../common/notification';
import {formatUtcDateTime} from '../../../common/dateTime';
import {CriticalActionDialog} from '../../../components/criticalActionDialog/CriticalActionDialog';
import {
    AdminExternalDeliveryOfferInfo,
    AdminExternalPurchaseInfo,
    ExternalPurchaseReviewReasonDescription,
    ExternalPurchaseReviewResolution,
    ExternalPurchaseReviewResolutionDescription,
    ExternalPurchaseReviewState,
} from '../../../models/externalMarkets';
import {OfferStatus, OfferStatusDescription} from '../../../models/order';
import {
    extPurchaseBindSteamOfferApi,
    extPurchaseRefreshApi,
    extPurchaseResolveApi,
} from '../../externalMarkets/externalMarketsApi';

/** Через сколько перечитать заказ после перепроверки: её выполняет BackgroundService */
const RefreshReloadDelayMs = 6000;

type Props = {
    purchase: AdminExternalPurchaseInfo;
    providerKey?: string;
    onChanged: () => void;
};

/**
 * Ручной разбор выкупа: почему открыт и что делать, хронология, оферы поставщика на бота и действия.
 * «Перепроверить» только читает данные поставщика; привязка Steam-офера и закрытие разбора меняют данные заказа
 * и проходят через подтверждение с предпросмотром и причиной.
 */
export function ExternalPurchaseReviewBlock(props: Props) {
    const {purchase, providerKey, onChanged} = props;
    const [bindOpen, setBindOpen] = React.useState(false);
    const [resolveOpen, setResolveOpen] = React.useState(false);
    const [refreshing, setRefreshing] = React.useState(false);

    const offers = purchase.deliveryOffers ?? [];
    const pendingOffers = offers.filter((o) => o.status === OfferStatus.Sent);
    const isOpen = purchase.reviewState === ExternalPurchaseReviewState.Open;
    const isResolved = purchase.reviewState === ExternalPurchaseReviewState.Resolved;
    if (!isOpen && !isResolved && pendingOffers.length === 0) {
        return null;
    }

    const reason = purchase.reviewReason ? ExternalPurchaseReviewReasonDescription[purchase.reviewReason] : undefined;
    const resolution = purchase.reviewResolution
        ? ExternalPurchaseReviewResolutionDescription[purchase.reviewResolution]
        : undefined;

    const refresh = async () => {
        if (!providerKey) {
            return;
        }
        setRefreshing(true);
        try {
            const res = await extPurchaseRefreshApi(providerKey, purchase.id);
            if (res.result) {
                NotificationService.alertSuccess('Перепроверка поставлена в очередь: результат появится в хронологии через несколько секунд');
                setTimeout(onChanged, RefreshReloadDelayMs);
            } else {
                NotificationService.alertError(res.error ?? 'Не удалось поставить перепроверку');
            }
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <Sheet variant="outlined" color={isOpen ? 'warning' : 'neutral'} sx={{p: 1.5, borderRadius: 'sm'}}>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography level="title-sm">Разбор выкупа</Typography>
                    {isOpen && <Chip size="sm" color="warning" variant="solid">Требует разбора</Chip>}
                    {isResolved && <Chip size="sm" color="success" variant="soft">Разобран</Chip>}
                    {!isOpen && !isResolved && <Chip size="sm" variant="soft">Ожидает доставку</Chip>}
                </Stack>

                {reason &&
                    <Alert color={isOpen ? 'warning' : 'neutral'} variant="soft">
                        <Stack spacing={0.5}>
                            <Typography level="title-sm">{reason.label}</Typography>
                            <Typography level="body-xs">Что делать: {reason.hint}</Typography>
                            {purchase.reviewOpenedAt &&
                                <Typography level="body-xs">Открыт {formatUtcDateTime(purchase.reviewOpenedAt)}</Typography>}
                        </Stack>
                    </Alert>}

                {isResolved &&
                    <Typography level="body-xs">
                        Итог: <b>{resolution?.label ?? purchase.reviewResolution}</b>
                        {purchase.reviewedBy ? `, закрыл ${purchase.reviewedBy}` : ''}
                        {purchase.reviewedAt ? ` ${formatUtcDateTime(purchase.reviewedAt)}` : ''}
                        {purchase.reviewNote ? `. Причина: ${purchase.reviewNote}` : ''}
                    </Typography>}

                {offers.length > 0 &&
                    <Stack spacing={0.25}>
                        <Typography level="body-xs" fontWeight="lg">Оферы поставщика на бота</Typography>
                        {offers.map((o) => <DeliveryOfferLine key={o.id} offer={o}/>)}
                    </Stack>}

                {purchase.reviewDetails &&
                    <Stack spacing={0.25}>
                        <Typography level="body-xs" fontWeight="lg">Хронология</Typography>
                        <Typography level="body-xs" component="pre"
                                    sx={{whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0, maxHeight: 220, overflowY: 'auto'}}>
                            {purchase.reviewDetails}
                        </Typography>
                    </Stack>}

                {providerKey &&
                    <Stack spacing={1}>
                        <ActionRow
                            button={<Button size="sm" variant="outlined" loading={refreshing} onClick={refresh}>
                                Перепроверить у поставщика
                            </Button>}
                            hint="Только читает статусы трейдов у поставщика и дописывает их в хронологию. Ничего не меняет — подтверждение не нужно."/>
                        {pendingOffers.length > 0 &&
                            <ActionRow
                                button={<Button size="sm" variant="outlined" color="warning" onClick={() => setBindOpen(true)}>
                                    Привязать Steam-офер…
                                </Button>}
                                hint="Когда поставщик не отдал номер Steam-офера, а у бота несколько одинаковых ожидающих оферов. Бот примет указанный Steam-офер для этого заказа."/>}
                        {isOpen &&
                            <ActionRow
                                button={<Button size="sm" variant="outlined" color="success" onClick={() => setResolveOpen(true)}>
                                    Закрыть разбор…
                                </Button>}
                                hint="Когда выяснили, чем закончилось: деньги вернулись, предметы пришли, списано в убыток. Статусы заказа не меняются."/>}
                    </Stack>}
            </Stack>

            {providerKey &&
                <BindSteamOfferDialog open={bindOpen} onClose={() => setBindOpen(false)} providerKey={providerKey}
                                      purchase={purchase} pendingOffers={pendingOffers} onDone={onChanged}/>}
            {providerKey &&
                <ResolveReviewDialog open={resolveOpen} onClose={() => setResolveOpen(false)} providerKey={providerKey}
                                     purchase={purchase} onDone={onChanged}/>}
        </Sheet>
    );
}

function ActionRow(props: { button: React.ReactNode; hint: string }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <div style={{flexShrink: 0}}>{props.button}</div>
            <Typography level="body-xs" color="neutral">{props.hint}</Typography>
        </Stack>
    );
}

function DeliveryOfferLine(props: { offer: AdminExternalDeliveryOfferInfo }) {
    const {offer} = props;
    const status = (OfferStatusDescription as Record<number, string>)[offer.status] ?? offer.status;
    return (
        <Typography level="body-xs">
            Офер #{offer.id}: {status}
            {offer.steamOfferId ? `, Steam-офер #${offer.steamOfferId}` : ', номер Steam-офера неизвестен'}
            {offer.botId ? `, бот #${offer.botId}` : ''}
            {offer.status === OfferStatus.Sent && offer.expireAt ? `, истекает ${formatUtcDateTime(offer.expireAt)}` : ''}
            {offer.lastError &&
                <Typography component="span" color="warning"> — {offer.lastError}</Typography>}
        </Typography>
    );
}

function BindSteamOfferDialog(props: {
    open: boolean;
    onClose: () => void;
    providerKey: string;
    purchase: AdminExternalPurchaseInfo;
    pendingOffers: AdminExternalDeliveryOfferInfo[];
    onDone: () => void;
}) {
    const {open, onClose, providerKey, purchase, pendingOffers, onDone} = props;
    const [offerId, setOfferId] = React.useState<number | null>(null);
    const [steamOfferId, setSteamOfferId] = React.useState('');

    React.useEffect(() => {
        if (open) {
            setOfferId(pendingOffers.length === 1 ? pendingOffers[0].id : null);
            setSteamOfferId('');
        }
    }, [open, pendingOffers]);

    const steamIdValid = /^\d{1,19}$/.test(steamOfferId.trim()) && !/^0+$/.test(steamOfferId.trim());
    const request = (confirm: boolean, reason?: string) => extPurchaseBindSteamOfferApi(providerKey, purchase.id, {
        offerId: offerId ?? 0,
        steamOfferId: steamOfferId.trim(),
        confirm,
        reason,
    });

    return (
        <CriticalActionDialog
            open={open}
            onClose={onClose}
            title="Привязка Steam-офера к заказу"
            description={<>
                Бот принимает входящий Steam-офер поставщика, только если однозначно знает, к какому заказу он относится.
                Укажите номер Steam-офера, который поставщик отправил <b>именно для заказа {purchase.orderId}</b>:
                возьмите его из трейда в истории аккаунта поставщика или из алерта бота. Ошибка отдаст предметы чужому
                заказу.
            </>}
            form={<Stack spacing={1.5}>
                <FormControl required>
                    <FormLabel>Офер поставщика этого заказа</FormLabel>
                    <Select size="sm" value={offerId} placeholder="Выберите офер"
                            onChange={(_, v) => setOfferId(typeof v === 'number' ? v : null)}>
                        {pendingOffers.map((o) =>
                            <Option key={o.id} value={o.id}>
                                Офер #{o.id}{o.steamOfferId ? ` (сейчас Steam-офер #${o.steamOfferId})` : ''}
                            </Option>)}
                    </Select>
                </FormControl>
                <FormControl required error={steamOfferId.length > 0 && !steamIdValid}>
                    <FormLabel>Номер Steam-офера</FormLabel>
                    <Input size="sm" value={steamOfferId} placeholder="Например, 7812345678"
                           onChange={(e) => setSteamOfferId(e.target.value)}/>
                    <FormHelperText>Только цифры, как в ссылке steamcommunity.com/tradeoffer/…</FormHelperText>
                </FormControl>
            </Stack>}
            formValid={offerId !== null && steamIdValid}
            loadPreview={() => request(false)}
            execute={(reason) => request(true, reason)}
            confirmLabel={`Привязать Steam-офер #${steamOfferId.trim()} к оферу #${offerId ?? ''}`}
            onDone={onDone}
        />
    );
}

function ResolveReviewDialog(props: {
    open: boolean;
    onClose: () => void;
    providerKey: string;
    purchase: AdminExternalPurchaseInfo;
    onDone: () => void;
}) {
    const {open, onClose, providerKey, purchase, onDone} = props;
    const [resolution, setResolution] = React.useState<ExternalPurchaseReviewResolution | null>(null);

    React.useEffect(() => {
        if (open) {
            setResolution(null);
        }
    }, [open]);

    const request = (confirm: boolean, reason?: string) => extPurchaseResolveApi(providerKey, purchase.id, {
        resolution: resolution ?? ExternalPurchaseReviewResolution.FalseAlarm,
        confirm,
        reason,
    });
    const label = resolution ? ExternalPurchaseReviewResolutionDescription[resolution].label : '';

    return (
        <CriticalActionDialog
            open={open}
            onClose={onClose}
            title="Закрытие разбора выкупа"
            description={<>
                Закрывайте разбор, когда выяснили по истории покупок и трейдов аккаунта поставщика, чем закончился
                выкуп заказа {purchase.orderId}. Итог и причина сохраняются в выкупе и журнале изменений; статусы заказа,
                баланс покупателя и оферы не меняются.
            </>}
            form={<FormControl required>
                <FormLabel>Итог</FormLabel>
                <RadioGroup value={resolution === null ? '' : String(resolution)}
                            onChange={(e) => setResolution(Number(e.target.value) as ExternalPurchaseReviewResolution)}>
                    {Object.entries(ExternalPurchaseReviewResolutionDescription).map(([value, d]) =>
                        <Radio key={value} value={value} label={<Stack>
                            <Typography level="body-sm">{d.label}</Typography>
                            <Typography level="body-xs" color="neutral">{d.hint}</Typography>
                        </Stack>}/>)}
                </RadioGroup>
            </FormControl>}
            formValid={resolution !== null}
            loadPreview={() => request(false)}
            execute={(reason) => request(true, reason)}
            confirmLabel={`Закрыть разбор: ${label}`}
            onDone={onDone}
        />
    );
}
